<template>
  <div class="performance-widget-container">
    <widget-header>{{ config.name || 'Questions Answered' }}</widget-header>
    <div class="widget-card widget-border widget-content">
      <div class="chart-wrapper">
        <div class="chart-form">
          <div class="date-range-chooser">
            <button
              v-for="option in dateRangeOptions"
              :key="option"
              class="date-range-option"
              :class="{ selected: option === dateRange}"
              :data-test="`date-range-${option.toLowerCase()}`"
              @click="selectRange(option)"
            >
              {{ option }}
            </button>
          </div>
        </div>
        <div class="chart-container">
          <bar-chart ref="bar-chart" :chart-data="chartData" :chart-options="chartOptions"></bar-chart>
        </div>
      </div>
      <div class="button-wrapper">
        <button data-test="decrement-date-offset" @click="offsetDecrement">
          <icon
            name="arrow-left-uui"
            variant="364152"
            class="arrow-icon"
          />
        </button>
        <span data-test="date-label" class="date-label">
          <icon
            name="calendar-uui"
            variant="364152"
            class="calendar-icon"
          />
          {{ dateLabel }}
        </span>
        <button
          data-test="increment-date-offset"
          :disabled="disableIncrement"
          :class="{ disabled: disableIncrement }"
          @click="offsetIncrement"
        >
          <icon
            name="arrow-right-uui"
            variant="364152"
            class="arrow-icon"
          />
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import WidgetHeader from '@components/dashboard-widgets/WidgetHeader';
import BarChart from '@/mixins/barChart.vue';
import Icon from '@components/singletons/icon';

const DATE_RANGE_OPTIONS = ['Week', 'Month', 'Year'];

export default {
  components: { WidgetHeader, BarChart, Icon },
  props: {
    config: {
      type: Object,
    },
    dashboard: {
      type: Object,
    },
  },
  data() {
    return {
      dateOffset: 0,
      dateRange: 'Week',
      dateRangeOptions: DATE_RANGE_OPTIONS,
    };
  },
  computed: {
    chartData() {
      return {
        labels: this.labels,
        datasets: [
          {
            label: 'Questions',
            backgroundColor: 'rgba(50, 111, 240, 1)',
            data: this.userData,
            borderRadius: {
              topLeft: 6,
              topRight: 6,
              bottomLeft: 0,
              bottomRight: 0,
            },
          },
        ],
      };
    },
    chartOptions() {
      const tickFontSize = this.dateRange === 'Year' ? 11 : this.dateRange === 'Month' ? 10 : 11;
      return {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: { top: 16, right: 8, bottom: 12, left: 8 },
        },
        plugins: {
          legend: {
            display: false,
          },
        },
        datasets: {
          bar: {
            categoryPercentage: this.dateRange === 'Year' ? 0.9 : this.dateRange === 'Month' ? 0.92 : 0.78,
            barPercentage: this.dateRange === 'Year' ? 0.92 : this.dateRange === 'Month' ? 0.88 : 0.9,
          },
        },
        scales: {
          x: {
            grid: {
              display: false,
              drawBorder: false,
            },
            ticks: {
              color: 'rgba(52, 64, 84, 1)',
              font: {
                size: tickFontSize,
                weight: '500',
              },
              padding: 8,
              maxRotation: this.dateRange === 'Month' ? 45 : 0,
              minRotation: 0,
              autoSkip: this.dateRange === 'Month',
              maxTicksLimit: this.dateRange === 'Month' ? 16 : undefined,
            },
            border: {
              display: false,
            },
          },
          y: {
            grid: {
              display: true,
              drawBorder: false,
              color: 'rgba(10, 13, 18, 0.06)',
              lineWidth: 1,
            },
            ticks: {
              beginAtZero: true,
              stepSize: this.stepSize,
              max: this.yAxisMax,
              padding: 6,
              color: 'rgba(113, 118, 128, 1)',
              font: {
                size: 11,
                weight: '500',
              },
            },
            border: {
              display: false,
            },
          },
        },
      };
    },
    stepSize() {
      const max = Math.max(...this.userData);
      const validSteps = [1, 2, 5, 10, 20, 50, 100, 200, 500, 1000];
      const index = validSteps.findIndex(step => step > max / 5);
      return validSteps[index];
    },
    yAxisMax() {
      const max = Math.max(...this.userData);
      const upperValue = max + (this.stepSize - (max % this.stepSize));
      return upperValue;
    },
    today() {
      return new Date();
    },
    rangeFirstDate() {
      if (this.dateRange === 'Week') {
        const offsetDate = new Date(this.today.getFullYear(), this.today.getMonth(), this.today.getDate() + this.dateOffset * 7);
        return this.firstDateOfWeek(offsetDate);
      } else if (this.dateRange === 'Month') {
        return new Date(this.today.getFullYear(), this.today.getMonth() + this.dateOffset, 1);
      } else if (this.dateRange === 'Year') {
        return new Date(this.today.getFullYear() + this.dateOffset, 0, 1);
      }
    },
    rangeLastDate() {
      if (this.dateRange === 'Week') {
        return this.changeDateByDays(this.rangeFirstDate, 6);
      } else if (this.dateRange === 'Month') {
        const firstDayOfNextMonth = new Date(this.rangeFirstDate.getFullYear(), this.rangeFirstDate.getMonth() + 1, 1);
        return this.changeDateByDays(firstDayOfNextMonth, -1);
      } else if (this.dateRange === 'Year') {
        return new Date(this.rangeFirstDate.getFullYear(), 11, 31);
      }
    },
    labels() {
      if (this.dateRange === 'Week') {
        return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      } else if (this.dateRange === 'Month') {
        const labels = [];
        for (let date = new Date(this.rangeFirstDate); date <= this.rangeLastDate; date.setDate(date.getDate() + 1)) {
          labels.push(`${date.getDate()}`);
        }
        return labels;
      } else if (this.dateRange === 'Year') {
        return ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];
      }
    },
    userFlashcardStats() {
      return this.$store.getters['user/stats'].flashcard || [];
    },
    userData() {
      if (!this.userFlashcardStats) {
        return [];
      }
      let data = [];

      if (this.dateRange === 'Month' || this.dateRange === 'Week') {
        for (let date = new Date(this.rangeFirstDate); date <= this.rangeLastDate; date.setDate(date.getDate() + 1)) {
          const count = this.userFlashcardStats.find((stat) => {
            return stat.date === this.dateFormatted(date);
          })?.count;
          data.push(count || 0);
        }
      } else if (this.dateRange === 'Year') {
        data = new Array(12).fill(0);
        this.userFlashcardStats.forEach(stat => {
          const currentYear = this.rangeFirstDate.getFullYear();
          const statYear = new Date(stat.date).getFullYear();
          if (currentYear !== statYear) {
            return;
          }
          const month = new Date(stat.date).getMonth();
          data[month] += stat.count;
        });
      }

      return data;
    },
    dateLabel () {
      if (this.dateRange === 'Week') {
        return `${this.dateFormattedForDateLabel(this.rangeFirstDate)} - ${this.dateFormattedForDateLabel(this.rangeLastDate)}`;
      } else if (this.dateRange === 'Month') {
        return `${this.rangeFirstDate.toLocaleString('default', { month: 'short' })} ${this.rangeFirstDate.getFullYear()}`;
      } else if (this.dateRange === 'Year') {
        return `${this.rangeFirstDate.getFullYear()}`;
      }
    },
    disableIncrement() {
      return this.dateOffset >= 0;
    },
  },
  methods: {
    dateFormatted(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based in JavaScript
      const day = String(date.getDate()).padStart(2, '0');

      return `${year}-${month}-${day}`;
    },
    dateFormattedForDateLabel(date) {
      const year = String(date.getFullYear() % 100).padStart(2, '0');
      const month = String(date.getMonth() + 1); // Months are 0-based in JavaScript
      const day = String(date.getDate());

      return `${month}/${day}/${year}`;
    },
    selectRange(range) {
      this.dateOffset = 0;
      this.dateRange = range;
    },
    changeDateByDays(date, days) {
      const result = new Date(date);
      result.setDate(result.getDate() + days);
      return result;
    },
    firstDateOfWeek(date) {
      const day = date.getDay();
      return new Date(date.getFullYear(), date.getMonth(), date.getDate() - day);
    },
    offsetIncrement() {
      if (this.dateOffset < 0) {
        this.dateOffset++;
      }
    },
    offsetDecrement() {
      this.dateOffset--;
    },
  },
};
</script>

<style scoped lang="scss">
.performance-widget-container {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.widget-card {
  /* Match footer strip so card chrome is one tone through the bottom */
  background: #FDFDFD;
  box-sizing: border-box;
  flex: 1;
  min-height: 0;
}

.widget-content {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: stretch;
}

.widget-border {
  border-radius: rem-calc(16);
  border: 1px solid var(--Colors-Border-border-secondary);
  box-shadow: 0 1px 2px 0 rgba(10, 13, 18, 0.05);
  overflow: hidden;
}

.chart-wrapper {
  background: var(--Colors-Foreground-fg-white);
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
  flex: 1;
  min-height: 0;
  padding: var(--spacing-xl) 16px var(--spacing-xl);
  border-radius: rem-calc(16);
  /* Bottom border follows this radius so the divider isn’t a straight line that hides the curve */
  border-bottom: 1px solid var(--Colors-Border-border-secondary);
  overflow: hidden;
}

.chart-form {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: var(--spacing-sm);
  width: 100%;
}

.chart-container {
  flex: 1 1 0;
  width: 100%;
  min-width: 0;
  min-height: rem-calc(132);
  position: relative;
}

.button-wrapper {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--spacing-md);
  padding: 12px 16px;
  box-sizing: border-box;
  width: 100%;
  min-height: rem-calc(60);
  background: #FDFDFD;

  > button,
  > .date-label {
    border: 1px solid #CDD5DF;
    border-radius: rem-calc(8);
    box-shadow: 0 1px 2px 0 rgba(16, 24, 40, 0.05);
    box-sizing: border-box;
  }

  button {
    display: grid;
    place-items: center;
    flex: 0 0 rem-calc(40);
    width: rem-calc(40);
    min-width: rem-calc(40);
    max-width: rem-calc(40);
    height: rem-calc(40);
    min-height: rem-calc(40);
    max-height: rem-calc(40);
    padding: 0;
    margin: 0;
    background-color: #FFF;
    color: #364152;

    &.disabled {
      cursor: auto;
      opacity: 0.5;
    }
  }

  .date-label {
    display: inline-flex;
    align-items: center;
    flex: 0 1 auto;
    flex-shrink: 0;
    gap: rem-calc(8);
    min-height: rem-calc(40);
    max-height: rem-calc(40);
    height: rem-calc(40);
    padding: 0 rem-calc(16);
    background-color: #FFF;
    white-space: nowrap;
    font-size: rem-calc(14);
    line-height: rem-calc(20);
    font-weight: 600;
    color: #364152;
  }

  .arrow-icon,
  .calendar-icon {
    display: block;
    object-fit: contain;
  }

  .arrow-icon {
    width: rem-calc(20);
    height: rem-calc(20);
    min-width: rem-calc(20);
    color: #364152;
  }

  .calendar-icon {
    flex-shrink: 0;
    width: rem-calc(20);
    height: rem-calc(20);
    min-width: rem-calc(20);
    color: #364152;
  }
}

.date-range-chooser {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  gap: var(--spacing-xxs);
  padding: var(--spacing-xxs);
  margin-bottom: 0;

  .date-range-option {
    min-width: rem-calc(52);
    height: rem-calc(30);
    padding: 0 var(--spacing-md);
    border: none;
    border-radius: var(--spacing-sm);
    color: var(--text-tertiary);
    background-color: transparent;
    font-weight: var(--Fontweightfont-weight-semibold);
    font-size: var(--Fontsizetext-sm);
    line-height: 1;
    cursor: pointer;
    transition: background-color 0.15s ease, color 0.15s ease;
  }

  .date-range-option.selected {
    background-color: #EFF4FF;
    color: var(--fg-brand-primary-600);
  }

  .date-range-option:not(.selected):hover {
    color: var(--text-secondary);
  }
}

@media screen and (max-width: 768px) {
  .chart-wrapper {
    padding: var(--spacing-sm) var(--spacing-sm) var(--spacing-lg);
  }

  .chart-container {
    min-height: rem-calc(176);
    max-height: rem-calc(280);
  }

  .chart-form {
    margin-bottom: var(--spacing-xs);
  }

  .date-range-chooser .date-range-option {
    min-width: rem-calc(44);
    height: rem-calc(28);
    padding: 0 var(--spacing-sm);
    font-size: var(--Fontsizetext-xs);
    border-radius: var(--spacing-sm);
  }

  .button-wrapper {
    justify-content: flex-end;
    padding: 12px 16px;
  }
}
</style>
